import { BLOCK_HEIGHT, BLOCK_TIME, DAY_START_BLOCK, DAY_END_BLOCK, HEADER_BLOCK_HEIGHT } from "../utils/constant"

export const getDayEnd = (tiles) =>
  Math.max(DAY_END_BLOCK, ...tiles.map((t) => t.end ?? 0))

export const isNoMachineCooling = (tile, noMachineTypeIds) => {
  if (tile?.productionStepSnapshot?.transformation !== "COOLING") return false
  const machineType = tile.productionStepSnapshot?.machineType
  if (!machineType) return true
  return noMachineTypeIds?.has(machineType.objectId) ?? false
}

export const findNeighbors = (sortedLane, newStart) => {
  let prevIdx = -1
  let nextIdx = sortedLane.length
  for (let i = 0; i < sortedLane.length; i++) {
    if (sortedLane[i].start < newStart) prevIdx = i
    else {
      nextIdx = i
      break
    }
  }
  return { prevIdx, nextIdx }
}

export const upCapacity = (lane, idx) => {
  if (idx < 0) return Infinity
  let cap = 0
  for (let i = idx; i >= 0; i--) {
    const above = i > 0 ? lane[i - 1] : null
    cap += lane[i].start - (above ? above.end : DAY_START_BLOCK)
  }
  return cap
}

export const downCapacity = (lane, idx, dayEnd) => {
  if (idx >= lane.length) return Infinity
  let cap = 0
  for (let i = idx; i < lane.length; i++) {
    const below = i < lane.length - 1 ? lane[i + 1] : null
    cap += (below ? below.start : dayEnd) - lane[i].end
  }
  return cap
}

export const findPrevIdxByTime = (sortedLane, t) => {
  let idx = -1
  for (let i = 0; i < sortedLane.length; i++) {
    if (sortedLane[i].start < t) idx = i
    else break
  }
  return idx
}

export const findNextIdxByTime = (sortedLane, t) => {
  for (let i = 0; i < sortedLane.length; i++) {
    if (sortedLane[i].start >= t) return i
  }
  return sortedLane.length
}

export const isValidPlannerTarget = (overId) => {
  return typeof overId === "string" && overId.startsWith("machine-")
}

export const parseMachineAndBlock = (overId) => {
  const [machineLabel, block] = overId.split("__")
  const machine = machineLabel.split("-")[1]
  const blockIndex = parseInt(block)
  return { machine, blockIndex }
}

export const getDropBlockRange = (tile, dropBlock) => {
  // Preserve the tile's current duration if it has been placed/resized
  // Otherwise default to 2 blocks for new unassigned tiles
  let duration = 2
  
  if (tile.start != null && tile.end != null) {
    duration = tile.end - tile.start
  } else if (tile.duration != null) {
    // Fallback to stored duration if start/end not defined
    duration = tile.duration
  }
  
  // Ensure minimum duration of 1 block
  duration = Math.max(1, duration)
  
  // Position the tile starting slightly above the drop block to align with cursor
  // Subtract 1 to compensate for the visual offset
  const startBlock = Math.max(0, dropBlock - 1)
  const endBlock = startBlock + duration
  return { startBlock, endBlock }
}

export const formatTileTimeRange = (start, end) => {
  const format = (block) => {
    const totalMinutes = block * BLOCK_TIME + 6 * 60 // Start at 6:00
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours.toString().padStart(2, "0")}h${minutes
      .toString()
      .padStart(2, "0")}`
  }

  return `${format(start)} – ${format(end)}`
}

export const getTilePosition = (tile, isDragging) => ({
  position: "absolute",
  top: tile.start * BLOCK_HEIGHT + HEADER_BLOCK_HEIGHT,
  height: (tile.end - tile.start) * BLOCK_HEIGHT,
  width: "calc(100% - 1px)",
  zIndex: isDragging ? 10 : 0,
  borderRadius: "4px",
  boxSizing: "border-box",
  paddingTop: "1px",
})

export const getHandleStyle = (position, backgroundColor) => ({
  position: "absolute",
  [position]: 0,
  left: 0,
  right: 0,
  height: 6,
  cursor: "ns-resize",
  backgroundColor: backgroundColor,
  zIndex: 2,
})

/**
 * Calculate tile height dimensions for responsive text layout
 */
export const getTileHeightMetrics = (tile) => {
  const tileHeight = (tile.end - tile.start) * BLOCK_HEIGHT
  const contentPadding = tileHeight <= BLOCK_HEIGHT ? "6px 8px" : "8px"
  const verticalPadding = tileHeight <= BLOCK_HEIGHT ? 12 : 16
  const uniqueCodeHeight = 12
  const lineHeight = 18
  const availableHeight = tileHeight - verticalPadding - uniqueCodeHeight
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight))
  
  return { tileHeight, contentPadding, maxLines }
}

/**
 * Find column width from DOM for a given machine
 */
export const getColumnWidthFromDOM = (machine) => {
  const machineColumn = document.querySelector(`[id^="machine-${machine}__"]`)
  if (machineColumn) {
    const columnElement = machineColumn.closest("[class*='machineColumn']") || 
                          machineColumn.closest("[class*='columnWrapper']")?.querySelector("[class*='machineColumn']") ||
                          machineColumn.parentElement
    if (columnElement) {
      return columnElement.offsetWidth
    }
  }
  return null
}

/**
 * Get the width of the first column in the planner table
 */
export const getFirstColumnWidth = () => {
  const firstColumn = document.querySelector("[id^='machine-']")
  if (firstColumn) {
    const columnElement = firstColumn.closest("[class*='machineColumn']") || 
                          firstColumn.closest("[class*='columnWrapper']")?.querySelector("[class*='machineColumn']") ||
                          firstColumn.parentElement
    if (columnElement) {
      return columnElement.offsetWidth
    }
  }
  return null
}

/**
 * Calculate drag offset information from event
 */
export const calculateDragOffset = (event) => {
  if (!event.activatorEvent) return null

  // Get the actual tile element
  const element = document.querySelector(`[data-id="${event.active.id}"]`) || 
                  event.activatorEvent.target.closest("[data-draggable='true']") || 
                  event.activatorEvent.target
  const rect = element.getBoundingClientRect()

  // Calculate where on the tile the user grabbed (as pixel offset and percentage)
  const initialWidth = rect.width
  const initialHeight = rect.height
  const offsetYPercentage = (event.activatorEvent.clientY - rect.top) / initialHeight
  const offsetXPercentage = (event.activatorEvent.clientX - rect.left) / initialWidth
  const initialXOffset = offsetXPercentage * initialWidth
  const initialYOffset = offsetYPercentage * initialHeight

  return {
    offsetXPercentage,
    initialWidth,
    initialXOffset,
    offsetYPercentage,
    initialHeight,
    initialYOffset
  }
}

/**
 * Initialize drag state for a tile
 */
export const initializeDragState = (tiles, event, setActiveTile, setColumnWidth, setExpandedTiles, expandedTiles) => {
  const draggedTile = tiles.find((t) => t.objectId === event.active.id)
  
  // Collapse "Voir plus" expansion for this tile when dragging starts
  if (draggedTile && expandedTiles[draggedTile.objectId]) {
    setExpandedTiles(prev => ({
      ...prev,
      [draggedTile.objectId]: false
    }))
  }
  
  // Initialize _isDraggedOverTable based on whether tile is currently assigned
  const isCurrentlyAssigned = draggedTile?.machine != null
  setActiveTile({ ...draggedTile, _isDraggedOverTable: isCurrentlyAssigned })

  // Get actual column width from the DOM
  if (draggedTile?.machine != null) {
    const width = getColumnWidthFromDOM(draggedTile.machine)
    if (width) setColumnWidth(width)
  } else {
    const width = getFirstColumnWidth()
    if (width) setColumnWidth(width)
  }

  return draggedTile
}

/**
 * Update tile appearance based on drag target
 */
export const updateTileAppearanceOnDrag = (activeTile, event, setActiveTile) => {
  if (!activeTile || !event.over) return

  const isOverUnassigned = event.over.id === "unassigned"
  const isOverTable = event.over.id.toString().includes("-")
  const isUnassignedTile = activeTile.machine == null
  const isAssignedTile = activeTile.machine != null

  if (isOverUnassigned && activeTile._isDraggedOverTable !== false) {
    // Hovering over unassigned panel - show unassigned appearance
    setActiveTile(prev => ({ ...prev, _isDraggedOverTable: false }))
  } else if (isOverTable && activeTile._isDraggedOverTable !== true && isUnassignedTile) {
    // Hovering over table with unassigned tile - show table appearance
    setActiveTile(prev => ({ ...prev, _isDraggedOverTable: true }))
  } else if (!isOverTable && !isOverUnassigned && activeTile._isDraggedOverTable !== false) {
    // No longer over table or unassigned - revert to unassigned appearance
    setActiveTile(prev => ({ ...prev, _isDraggedOverTable: false }))
  } else if (isOverTable && isAssignedTile && activeTile._isDraggedOverTable !== true) {
    // Assigned tile over table - ensure it shows as assigned
    setActiveTile(prev => ({ ...prev, _isDraggedOverTable: true }))
  }
}

/**
 * Returns true if the tile matches the query (for both merged and non-merged steps)
 */
export function customUnassignedTilesFilter(tile, query) {
  const q = query.toLowerCase()
  // Match by objectId (for exact selection)
  if ((tile.objectId || "").toLowerCase() === q) return true
  
  if (tile.isStepMerged && tile.isStepMergedDict) {
    for (const [uniqueCode, merged] of Object.entries(tile.isStepMergedDict)) {
      if (
        (uniqueCode && uniqueCode.toLowerCase().includes(q)) ||
        (merged.sectionName && merged.sectionName.toLowerCase().includes(q)) ||
        (merged.recipeName && merged.recipeName.toLowerCase().includes(q))
      ) {
        return true
      }
    }
    if ((tile.PSEName || "").toLowerCase().includes(q)) return true
    return false
  }
  return (
    (tile.uniqueCode || "").toLowerCase().includes(q) ||
    (tile.sectionName || "").toLowerCase().includes(q) ||
    (tile.recipeName || "").toLowerCase().includes(q) ||
    (tile.PSEName || "").toLowerCase().includes(q)
  )
}

/**
 * Filters an array of tiles by query
 */
export function filterUnassignedTiles(tiles, query) {
  if (!query.trim()) return tiles
  return tiles.filter(tile => customUnassignedTilesFilter(tile, query))
}

/**
 * Autocomplete filter for options
 */
export function unassignedTilesSearchOption(option, query) {
  return customUnassignedTilesFilter(option.value, query)
}

/**
 * Returns autocomplete options for unassigned tiles
 */
export function getUnassignedAutocompleteOptions(tiles) {
  return tiles.map((tile) => ({
    label: tile.isStepMerged
      ? `${Object.keys(tile.isStepMergedDict || {}).join(" + ")} / ${tile.PSEName}`
      : `${tile.uniqueCode} - ${tile.sectionName} / ${tile.PSEName}`,
    value: tile,
    key: tile.objectId,
  }))
}

